import { BetrokkenTrein, usePatchTreinEvacuatie, usePostTreinEvacuatie } from "@/entities/betrokken-treinen";
import { TreinEvacuatieVerwijderenDialog } from "@/features/betrokken-treinen/components/evacuatie-trein/trein-evacuatie-verwijderen-dialog.tsx";
import { StationMultiSelect } from "@/features/infra/components/stations-multiselect.tsx";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormFieldInput,
} from "@/shared/components";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const addOrUpdateTreinEvacuatieSchema = z.object({
    vertrekTijd: z.iso.time({ error: "Vul de vertrektijd in als HH:MM" }),
    afzetStations: z
        .array(z.string(), { error: (issue) => (issue.input === undefined ? "Vul minimaal 1 afzetstation in" : undefined) })
        .min(1, "Vul minimaal 1 afzetstation in")
        .max(10, "Selecteer maximaal 10 stations"),
    toelichting: z.string().optional(),
});

type AddOrUpdateTreinEvacuatieData = z.infer<typeof addOrUpdateTreinEvacuatieSchema>;

type TreinEvacuatieToevoegenOfBewerkenDialogProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    dossierId: string;
    trein: BetrokkenTrein;
};

export function AddOrUpdateTreinEvacuatieDialog({ open, setOpen, dossierId, trein }: TreinEvacuatieToevoegenOfBewerkenDialogProps) {
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const postTreinEvacuatie = usePostTreinEvacuatie();
    const patchTreinEvacuatie = usePatchTreinEvacuatie();
    const isEdit = !!trein.treinEvacuatie;

    const form = useForm<AddOrUpdateTreinEvacuatieData>({
        resolver: zodResolver(addOrUpdateTreinEvacuatieSchema),
        defaultValues: {
            vertrekTijd: trein.treinEvacuatie?.vertrekTijd ?? "",
            afzetStations: trein.treinEvacuatie?.afzetStations ?? [],
            toelichting: trein.treinEvacuatie?.toelichting ?? "",
        },
    });

    const onOpenChange = (open: boolean) => {
        if (!open) {
            form.reset();
        }
        setOpen(open);
    };

    const { isDirty, isValid } = form.formState;
    const onSubmit = (data: AddOrUpdateTreinEvacuatieData) => {
        if (!isDirty) {
            setOpen(false);
            return;
        }
        if (isEdit) {
            patchTreinEvacuatie.mutate(
                { dossierId, treinId: trein.id, ...data },
                {
                    onSuccess: () => setOpen(false),
                }
            );
        } else {
            postTreinEvacuatie.mutate(
                { dossierId, treinId: trein.id, ...data },
                {
                    onSuccess: () => setOpen(false),
                }
            );
        }
    };

    const isPending = postTreinEvacuatie.isPending || patchTreinEvacuatie.isPending;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <RadixBaseUiInteropContextProvider radixElement={dialogRef}>
                <DialogContainer className="min-w-[580px]" ref={dialogRef}>
                    <DialogHeader className="border-b pb-3">
                        <DialogTitle>
                            Evacuatietrein {isEdit ? "aanpassen" : "toevoegen"} {BetrokkenTrein.formatTrainLabel(trein)}
                        </DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                            <DialogBody className="grid gap-4">
                                <FormFieldInput
                                    form={form}
                                    name="vertrekTijd"
                                    label="Vertrektijd"
                                    type="time"
                                    helpText="Wanneer is de evacuatietrein bij de incidentlocatie vertrokken?"
                                />
                                <StationMultiSelect
                                    form={form}
                                    name="afzetStations"
                                    label="Afzetstations"
                                    placeholder="Selecteer afzetstations"
                                    badgeLabel="value"
                                    helpText="Op welke afzetstations stopt de evacuatietrein? Voeg de stations in rijvolgorde toe."
                                    triggerFormValidationOnChange
                                />
                                <FormFieldInput
                                    form={form}
                                    name="toelichting"
                                    label="Toelichting"
                                    type="text"
                                    placeholder="Voeg een toelichting toe"
                                    optional
                                />
                                {postTreinEvacuatie.error && <ErrorDisplay error={postTreinEvacuatie.error} />}
                                {patchTreinEvacuatie.error && <ErrorDisplay error={patchTreinEvacuatie.error} />}
                            </DialogBody>
                            <DialogFooter>
                                {isEdit && (
                                    <Button
                                        type="button"
                                        variant="tertiary"
                                        destructive
                                        className="mr-auto"
                                        onClick={() => setDeleteConfirmationOpen(true)}
                                    >
                                        Evacuatietrein verwijderen
                                    </Button>
                                )}
                                <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
                                    Annuleren
                                </Button>
                                <Button type="submit" variant="primary" disabled={isPending || !isValid}>
                                    {isEdit ? "Opslaan" : "Toevoegen"}
                                </Button>
                            </DialogFooter>
                            {deleteConfirmationOpen && (
                                <TreinEvacuatieVerwijderenDialog
                                    dossierId={dossierId}
                                    trein={trein}
                                    open={deleteConfirmationOpen}
                                    onOpenChange={setDeleteConfirmationOpen}
                                />
                            )}
                        </form>
                    </Form>
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}
